import type { Recipe } from '../../types'
import { image, ingredient, section } from '../helpers'

export const lasagnesRicottaEpinards: Recipe = {
  slug: 'lasagnes-ricotta-epinards',
  title: 'Lasagnes ricotta–épinards',
  category: 'salé',
  description: 'Des lasagnes fraîches et fondantes, garnies d’épinards, de ricotta et de béchamel au parmesan.',
  image: image('lasagnes-ricotta-epinards'),
  tags: ['épinards', 'ricotta', 'lasagnes'],
  meta: [
    { label: 'Cuisson', value: '30 à 35 min à 180 °C' },
    { label: 'Repos', value: '15 min' },
  ],
  sections: [
    section('Épinards et ricotta', [
      ingredient(
        'epinards',
        'épinards frais',
        '800 g d’épinards frais (ou environ 450 à 500 g d’épinards surgelés, bien décongelés et fortement essorés)',
        { amount: 800, unit: 'g', shoppingKey: 'épinards', shoppingLabel: 'épinards frais (ou surgelés)' },
      ),
      ingredient('ricotta', 'ricotta', '500 g de ricotta', {
        amount: 500,
        unit: 'g',
        shoppingKey: 'ricotta',
      }),
      ingredient('oignons', 'oignons', '2 oignons', {
        amount: 2,
        unit: 'oignon',
        shoppingKey: 'oignon',
      }),
      ingredient('echalotes', 'échalotes', '2 échalotes', {
        amount: 2,
        unit: 'échalote',
        shoppingKey: 'échalote',
      }),
      ingredient('jaunes-oeufs', 'jaunes d’œufs', '2 jaunes d’œufs', {
        amount: 2,
        shoppingKey: 'jaunes d’œufs',
      }),
      ingredient('parmesan-appareil', 'parmesan râpé', '100 g de parmesan râpé dans les épinards', {
        amount: 100,
        unit: 'g',
        shoppingKey: 'parmesan',
      }),
      ingredient('vin-blanc', 'vin blanc sec', '15 à 20 cl de vin blanc sec', {
        shoppingKey: 'vin blanc sec',
        shoppingLabel: 'vin blanc sec (15 à 20 cl)',
      }),
      ingredient('beurre', 'beurre', 'Beurre', { shoppingKey: 'beurre' }),
      ingredient('huile-olive', 'huile d’olive', 'Huile d’olive', { shoppingKey: 'huile d’olive' }),
      ingredient('assaisonnement', 'sel, poivre et noix de muscade', 'Sel, poivre et noix de muscade'),
      ingredient('citron', 'jus de citron', 'Un trait de jus de citron pour rectifier l’acidité', {
        shoppingKey: 'citron',
        shoppingLabel: 'jus de citron',
      }),
    ]),
    section('Béchamel', [
      ingredient('lait', 'lait', 'Environ 550 à 600 ml de lait', {
        shoppingKey: 'lait',
        shoppingLabel: 'lait (550 à 600 ml)',
      }),
      ingredient('beurre-bechamel', 'beurre', '40 g de beurre', {
        amount: 40,
        unit: 'g',
        shoppingKey: 'beurre',
      }),
      ingredient('farine', 'farine', '40 g de farine', { amount: 40, unit: 'g', shoppingKey: 'farine' }),
      ingredient('parmesan-bechamel', 'parmesan râpé', 'Environ 70 g de parmesan râpé dans la béchamel', {
        amount: 70,
        unit: 'g',
        shoppingKey: 'parmesan',
      }),
      ingredient('assaisonnement-bechamel', 'sel, poivre et noix de muscade', 'Sel, poivre et noix de muscade'),
    ]),
    section('Montage', [
      ingredient('pates-lasagnes', 'pâte fraîche à lasagnes', 'Pâte fraîche à lasagnes, selon la taille du plat', {
        shoppingKey: 'pâte fraîche à lasagnes',
      }),
      ingredient('parmesan-montage', 'parmesan râpé', 'Parmesan râpé entre les couches et sur le dessus', {
        shoppingKey: 'parmesan',
      }),
      ingredient('fromage-gratiner', 'comté râpé', 'Comté râpé, éventuellement remplacé par de l’emmental, pour gratiner', {
        shoppingKey: 'fromage à gratiner',
        shoppingLabel: 'comté râpé (ou emmental)',
      }),
    ]),
  ],
  steps: [
    'Émincer finement les oignons et les échalotes. Les faire revenir doucement dans un mélange de beurre et d’huile d’olive pendant une vingtaine de minutes, jusqu’à ce qu’ils soient bien fondants et légèrement colorés.',
    'Déglacer avec 15 à 20 cl de vin blanc sec en grattant bien les sucs, puis laisser réduire presque complètement.',
    'Ajouter les épinards (bien lavés et séchés) progressivement. Les faire tomber rapidement puis poursuivre la cuisson jusqu’à évaporation complète de l’eau. Avec un wok, repousser sur les bords les épinards déjà tombés pendant que les suivants cuisent au centre ; idéalement, les ajouter assez rapidement afin d’éviter de trop cuire les premières fournées.',
    'Poivrer généreusement, ajouter de la noix de muscade et rectifier le sel. Laisser refroidir puis hacher grossièrement les épinards.',
    'Préparer une béchamel avec 40 g de beurre, 40 g de farine et environ 550 à 600 ml de lait. La garder assez épaisse, l’assaisonner de muscade et de poivre, puis incorporer environ 70 g de parmesan.',
    'Mélanger les épinards avec les 500 g de ricotta, la béchamel et 100 g de parmesan râpé. Ajouter un trait de jus de citron pour rectifier l’acidité. Une fois la préparation refroidie, incorporer les deux jaunes d’œufs.',
    'Rectifier soigneusement l’assaisonnement : la préparation doit être assez relevée, car les pâtes vont adoucir l’ensemble.',
    'Monter les lasagnes en alternant pâte fraîche, appareil ricotta–épinards et parmesan râpé. Terminer par une fine couche d’appareil, du parmesan râpé, puis du comté râpé (éventuellement de l’emmental) pour gratiner.',
    'Cuire environ 30 à 35 minutes à 180 °C, puis faire gratiner quelques minutes si nécessaire. Laisser reposer 15 minutes avant de servir.',
  ],
}
